import "./CreateExhibition.css"
import DashboardHeader from "../../components/dashboardHeader/DashboardHeader";
import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import PrimaryLabel from "../../components/primaryLabel/PrimaryLabel";
import PrimaryInput from "../../components/primaryInput/PrimaryInput";
import PrimaryButton from "../../components/primaryButton/PrimaryButton";
import {postExhibition} from "../../services/api/Exhibition";
import {uploadFile} from "../../services/api/File";
import useAccessToken from "../../hooks/useToken";
import interruptAuth from "../../utils/Interrupt_auth";
import {ExhibitionForm} from "./ExhibitionForm";
import {MediaFormField, MediaFileContent} from "../../components/mediaFormField/MediaFormField";
import FileFormField from "../../components/fileFormField/FileFormField";
import {copy} from "../../utils/copy";
import {createExhibitionFilePath, getFileExtension} from "../../utils/file";
import {useDashboardLocale} from "../../context/dashboard_locale";
import {capitalize} from "../../utils/strings";
import {MediaMimeTypes} from "../../services/file/file";
import {displayDialogMessage} from "../../utils/display_dialog_message";

export default function CreateExhibition() {
    let {token} = useAccessToken()
    const {strings} = useDashboardLocale()
    const navigate = useNavigate()
    let [exhibitionForm, setExhibitionForm] = useState(new ExhibitionForm())
    let [exhibitionErrorMessage, setExhibitionErrorMessage] = useState(null);
    let [isLoading, setIsLoading] = useState(false);

    useEffect(function() {
        interruptAuth(token, navigate)
    }, []);

    useEffect(() => {
        if (exhibitionErrorMessage) {
            displayDialogMessage(capitalize(strings["error"]), exhibitionErrorMessage, strings["ok"]);
        }
    }, [exhibitionErrorMessage, strings]);

    let mediaFormFieldJSXs = exhibitionForm.mediaOptions.map(function (mediaOption) {
      const file = exhibitionForm.mediaFiles[mediaOption.id];
      const audioFileContent = new MediaFileContent(
          mediaOption.id,
          file,
          mediaOption.titleFile,
          mediaOption.originName
      );
      return (
          <MediaFormField
              key={mediaOption.id}
              mediaFileContent={audioFileContent}
              accept="audio/*,video/*,image/*"
              fileNameValidationError={strings[mediaOption.errLocalizedKey] ?? mediaOption.remoteErrorMessage}
              deleteDisabled={exhibitionForm.mediaOptions.length <= 1}
              onChange={onChangeMediaFormField}
              onDelete={onDeleteMediaFile}
          />
      );
    })

    let fileFormFieldJSXs = exhibitionForm.fileOptions.map(function(item) {
        return (
            <FileFormField
                key={item.id}
                id={item.id}
                title={strings[item.localizedKey]}
                errorMessage={item.remoteErrorMessage}
                onChange={onChangeImageFile}
                required={true}
            />
        )
    })

    function onDeleteMediaFile(id) {
        const copyExhibitionForm = copy(exhibitionForm);
        copyExhibitionForm.removeMediaOption(id);
        setExhibitionForm(copyExhibitionForm);
    }

    function onChangeClientCode(e) {
        const copyExhibitionForm = copy(exhibitionForm);
        copyExhibitionForm.clientCode = e.target.value;
        setExhibitionForm(copyExhibitionForm);
    }

    function onChangeProductCode(e) {
        const copyExhibitionForm = copy(exhibitionForm)
        copyExhibitionForm.productCode = e.target.value;
        setExhibitionForm(copyExhibitionForm);
    }

    function onChangeImageFile({id, file}) {
        const copyExhibitionForm = copy(exhibitionForm)
        console.log(MediaMimeTypes.getMediaTypeCategoryFromFile(file));
        copyExhibitionForm.imageFiles[id] = file
        copyExhibitionForm.fileOptions = copyExhibitionForm.fileOptions.map(option => {
            if (option.id !== id) {
                return option;
            }
            return {...option, remoteErrorMessage: null};
        });
        setExhibitionForm(copyExhibitionForm);
    }

    function onChangeMediaFormField({id, file, titleFile, originName}) {
      const copyExhibitionForm = copy(exhibitionForm);
      delete copyExhibitionForm.mediaFiles[id];
      copyExhibitionForm.mediaFiles[id] = file;
      copyExhibitionForm.mediaOptions = copyExhibitionForm.mediaOptions.map(option => {
          if (option.id !== id) {
              return option;
          }
          return {...option, remoteErrorMessage: null};
      });
      copyExhibitionForm.updateMediaOption(id, titleFile, originName);
      setExhibitionForm(copyExhibitionForm);
    }

    function onAddMediaOption(e) {
      e.preventDefault();

      const copyExhibitionForm = copy(exhibitionForm);
      copyExhibitionForm.addNewMediaOption();
      setExhibitionForm(copyExhibitionForm);
    }

    function onFormSubmit(e) {
        function FileNetworkModel(fieldId, tag, path, fileName, priority, file) {
            return {
                fieldId: fieldId,
                tag: tag,
                path: path,
                titleFile: fileName,
                priority: priority,
                file: file,
            }
        }

        e.preventDefault();
        setExhibitionErrorMessage(null);
        const validationError = exhibitionForm.validateFields()
        if (validationError) {
            setExhibitionErrorMessage(capitalize(strings[validationError.localizedKey]));
            return
        }

        let networkModel = {
            "client_code": exhibitionForm.clientCode,
            "product_code": exhibitionForm.productCode,
        }
        const mediaFileNetworkModels = exhibitionForm.mediaOptions.map((mediaOption, idx) => {
          const id = mediaOption.id;
          const fileName = mediaOption.titleFile;
          const file = exhibitionForm.mediaFiles[mediaOption.id];
          const tag = MediaMimeTypes.getMediaTypeCategoryFromFile(file);
          const ext = getFileExtension(file.name)
          const priority = exhibitionForm.mediaOptions.length - idx;
          const path = createExhibitionFilePath(exhibitionForm.clientCode, exhibitionForm.productCode, "media", fileName, ext);
          return FileNetworkModel(id, tag, path, fileName, priority, file);
        })

        const imageFileNetworkModels = Object.entries(exhibitionForm.imageFiles).map(([key, file]) => {
            const ext = getFileExtension(file.name)
            const priority = 0;
            const tag = MediaMimeTypes.getMediaTypeCategoryFromFile(file);
            const path = createExhibitionFilePath(exhibitionForm.clientCode, exhibitionForm.productCode, "image", key, ext);
            return FileNetworkModel(key, tag, path, key, priority, file);
        })

        const fileNetworkModels = mediaFileNetworkModels.concat(imageFileNetworkModels);
        const uploadedFiles = fileNetworkModels.map(item => {
            return uploadFile(token, item.path, item.titleFile, item.priority, item.file)
                .then(res => {
                    return res.data["response"]["id"];
                })
                .then(id => {
                    item.id = id;
                    return item;
                })
                .catch(err => {
                    // Link each upload error to the appropriate file field.
                    const isMediaFile = mediaFileNetworkModels.some(model => item.fieldId === model.fieldId)
                    const errorMessage = err.response?.data?.["error_message"] ?? "File upload failed";
                    setExhibitionForm(currentForm => {
                        const copyExhibitionForm = copy(currentForm);
                        if (isMediaFile) {
                            copyExhibitionForm.mediaOptions = copyExhibitionForm.mediaOptions.map(option => {
                                if (option.id !== item.fieldId) {
                                    return option;
                                }
                                return {...option, remoteErrorMessage: errorMessage};
                            });
                        } else {
                            copyExhibitionForm.fileOptions = copyExhibitionForm.fileOptions.map(option => {
                                if (option.id !== item.fieldId) {
                                    return option;
                                }
                                return {...option, remoteErrorMessage: errorMessage};
                            });
                        }
                        return copyExhibitionForm;
                    });
                    throw err;
                })
        })

        setIsLoading(true)
        Promise.all(uploadedFiles)
            .then(updatedFileNetworkModels => {
                let mediaNetworkResponses = []
                let imageNetworkResponses = []
                updatedFileNetworkModels.forEach(function (updatedFileNetworkModel) {
                    let isMediaFile = mediaFileNetworkModels.some((mediaFileNetworkModel) => {
                        return mediaFileNetworkModel.path === updatedFileNetworkModel.path
                    });
                    if (isMediaFile) {
                        mediaNetworkResponses.push(updatedFileNetworkModel);
                    } else {
                        imageNetworkResponses.push(updatedFileNetworkModel);
                    }
                })
                let linkMediaNetworkModels = mediaNetworkResponses.map((mediaNetworkResponse) => {
                    return {
                        "file_id": mediaNetworkResponse.id,
                        "tag": mediaNetworkResponse.tag,
                    }
                })
                let linkImageNetworkModels = imageNetworkResponses.map((imageNetworkResponse) => {
                    return {
                        "file_id": imageNetworkResponse.id,
                        "tag": imageNetworkResponse.tag,
                    }
                })
                networkModel.medias = linkMediaNetworkModels
                networkModel.images = linkImageNetworkModels
                return networkModel
            }).then(exhibition => {
                return postExhibition(exhibition, token).catch(error => {
                    const errMessage = error.response?.data?.["error_message"] ?? "Failed to create exhibition"
                    setExhibitionErrorMessage(errMessage);
                    throw error;
                })
            }).then(_ => {
                navigate("/dashboard/exhibitions");
            })
            .catch( err => console.log(err))
            .finally(() => {
                setIsLoading(false)
            })
    }

    return (
        <>
            <DashboardHeader />
            <form>
                <div className="h-screen flex flex-wrap justify-center items-center">
                    <div className="container shadown-md bg-white text-left rounded px-8 pt-6 pb-8 mb-4">
                        <h4 className="text-lg text-center font-bold c-primary">{capitalize(strings["create_exhibition_title"])}</h4>
                        <div>
                            <h4 className="text-md text-left font-bold c-primary mb-2">{strings["exhibition_info"]}</h4>
                            <div className="mb-5">
                                <PrimaryLabel>{strings["client_code"]}<span className={"text-red-500 font-medium"}>*</span></PrimaryLabel>
                                <PrimaryInput
                                    name="client_code"
                                    type="text"
                                    placeholder={strings["client_code_prompt"]}
                                    onChange={onChangeClientCode}
                                >
                                    {exhibitionForm.clientCode}
                                </PrimaryInput>
                            </div>
                            <div className="mb-5">
                                <PrimaryLabel>{strings["product_code"]}<span className={"text-red-500 font-medium"}>*</span></PrimaryLabel>
                                <PrimaryInput
                                    name="product_code"
                                    type="text"
                                    placeholder={strings["product_code_prompt"]}
                                    onChange={onChangeProductCode}
                                >
                                    {exhibitionForm.productCode}
                                </PrimaryInput>
                            </div>
                        </div>
                        <div className="mb-8">
                            <h4 className="text-md text-left font-bold c-primary mb-2">{capitalize(strings["languages"])}<span className={"text-red-500 font-medium"}>*</span></h4>
                            <div className="mb-3">
                                {mediaFormFieldJSXs}
                            </div>
                            <div>
                                <PrimaryButton
                                    onClick={onAddMediaOption}
                                    disabled={!exhibitionForm.canAddNewMediaOption()}>
                                    {capitalize(strings["add_language"])}
                                </PrimaryButton>
                            </div>
                        </div>
                        <div className="mb-5">
                            <h4 className="text-md text-left font-bold c-primary mb-2">{capitalize(strings["screens"])}</h4>
                            <div>
                                {fileFormFieldJSXs}
                            </div>
                        </div>
                        <div className="mt-4">
                            <PrimaryButton
                                disabled={isLoading}
                                extraClassName="w-full"
                                onClick={onFormSubmit}
                                isLoading={isLoading}
                            >
                                {capitalize(strings["send"])}
                            </PrimaryButton>
                        </div>
                    </div>
                </div>
            </form>
        </>
    );
}
